import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { shortenAddress } from '../../../helpers/format';
import { copy } from '../../../helpers/copy';
import whiteCopyIcon from '../../../assets/icons/generalIcons/whiteCopyIcon2.svg';
import linkIcon from '../../../assets/icons/generalIcons/linkIcon.svg';
import { explorerLinks, ZERO_ADDRESS } from '../../../helpers/constants';
import { GET_TOP30_USERS } from '../../../helpers/graphRequests';
import { fetchUserIdsByAddresses } from '../../../helpers/fetchUserIds';
import { UserIdBlock } from '../../../components/UserIdBlock';
import { UserAvatar } from '../../../components/UserAvatar';
import { useUserProfiles } from '../../../helpers/hooks/useUserProfiles';
import { useWeb3React } from '@web3-react/core';
import { Pulse } from '../Pulse';

const toIdMap = (users = []) => {
  const map = {};
  users.forEach((user) => {
    const id = Number(user?.internalId);
    if (!user?.id || !Number.isFinite(id) || id <= 0) {
      return;
    }
    map[String(user.id).toLowerCase()] = id;
  });
  return map;
};

const TABS = {
  DUO: 'duo',
  QUATTRO: 'quattro',
};

export const Top30Table = ({ isLoading = false, passiveData }) => {
  const { account, provider } = useWeb3React();
  const [currentTab, setCurrentTab] = useState(TABS.DUO);
  const [addressToIdMap, setAddressToIdMap] = useState({});
  const [getTop30Users] = useLazyQuery(GET_TOP30_USERS, {
    fetchPolicy: 'network-only',
  });

  const top30List = useMemo(() => {
    const data = currentTab === TABS.DUO ? passiveData?.x2?.top30 : passiveData?.x4?.top30;
    if (!Array.isArray(data)) return [];

    const seenAddresses = new Set();
    const unique = [];

    data.forEach((address) => {
      if (!address || address === ZERO_ADDRESS) return;

      const key = address.toLowerCase();
      if (seenAddresses.has(key)) return;

      seenAddresses.add(key);
      unique.push(address);
    });

    return unique.slice(0, 30);
  }, [currentTab, passiveData?.x2?.top30, passiveData?.x4?.top30]);

  useEffect(() => {
    if (!top30List.length) {
      setAddressToIdMap({});
      return undefined;
    }

    let cancelled = false;

    const walletProvider = account && provider ? provider.getSigner(account).connectUnchecked() : provider || null;
    const addresses = top30List.map((addr) => addr.toLowerCase());

    Promise.all([
      fetchUserIdsByAddresses(top30List, walletProvider).catch(() => ({})),
      getTop30Users({ variables: { addresses } })
        .then((result) => toIdMap(result?.data?.users))
        .catch(() => ({})),
    ]).then(([rpcMap, graphMap]) => {
      if (cancelled) {
        return;
      }

      const merged = {};
      addresses.forEach((addr) => {
        merged[addr] = rpcMap[addr] ?? graphMap[addr] ?? null;
      });
      setAddressToIdMap(merged);
    });

    return () => {
      cancelled = true;
    };
  }, [top30List, account, provider, getTop30Users]);

  // Получаем ID для адреса
  const getUserId = (address) => {
    return addressToIdMap[address?.toLowerCase()] || null;
  };

  const uniqueTop30List = useMemo(() => {
    const seenIds = new Set();

    return top30List.filter((address) => {
      const userId = addressToIdMap[address?.toLowerCase()];
      if (userId == null) return true;
      if (seenIds.has(userId)) return false;
      seenIds.add(userId);
      return true;
    });
  }, [top30List, addressToIdMap]);

  const profiles = useUserProfiles(uniqueTop30List);
  const scrollerRef = useRef(null);
  const [canScroll, setCanScroll] = useState({ right: false, bottom: false });

  const updateScrollHints = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) {
      return;
    }

    setCanScroll({
      right: el.scrollWidth - el.clientWidth - el.scrollLeft > 2,
      bottom: el.scrollHeight - el.clientHeight - el.scrollTop > 2,
    });
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) {
      return undefined;
    }

    updateScrollHints();
    const observer = new ResizeObserver(updateScrollHints);
    observer.observe(el);
    if (el.firstElementChild) {
      observer.observe(el.firstElementChild);
    }

    return () => observer.disconnect();
  }, [uniqueTop30List, updateScrollHints]);

  return (
    <div className="flex flex-col w-full space-y-[2rem] lg:space-y-[1.6rem]">
      <div className="flex flex-col space-y-[2rem] w-full bg-white-40 border-[0.1rem] border-solid border-white-50 rounded-[3rem] p-[2rem] backdrop-blur-[1.5rem] lg:border-none lg:p-[1.6rem]">
        <div className="flex items-center justify-between">
          <h2 className="text-[2rem] font-600 text-white lg:text-[1.8rem]">Weekly Top-30</h2>
        </div>

        {/* Tabs */}
        <div className="flex p-[0.4rem] bg-white-80 rounded-[1.2rem] h-[4.8rem] shrink-0 w-full">
          <button
            onClick={() => setCurrentTab(TABS.DUO)}
            className={`flex items-center justify-center flex-1 text-white ${
              currentTab === TABS.DUO ? 'bg-[rgba(2,2,4,0.50)]' : 'opacity-50'
            } rounded-[0.8rem] custom-transition hover:opacity-70`}
          >
            <span className="text-[1.6rem] font-500">Duo</span>
          </button>
          <button
            onClick={() => setCurrentTab(TABS.QUATTRO)}
            className={`flex items-center justify-center flex-1 text-white ${
              currentTab === TABS.QUATTRO ? 'bg-[rgba(2,2,4,0.50)]' : 'opacity-50'
            } rounded-[0.8rem] custom-transition hover:opacity-70`}
          >
            <span className="text-[1.6rem] font-500">Quattro</span>
          </button>
        </div>

        {/* Table */}
        <div className="relative w-full">
          <div
            ref={scrollerRef}
            onScroll={updateScrollHints}
            className="hide-scroll max-h-[48rem] overflow-auto lg:max-h-[42rem]"
          >
            <div className="min-w-[72rem]">
              <div className="sticky top-0 z-10 mb-[1.6rem] flex h-[3.2rem] items-center gap-[1.2rem] bg-[#16141c] px-[1.6rem] text-[1.4rem] font-500 text-white-300 lg:h-auto lg:px-[1.2rem] lg:text-[1.2rem] lg:leading-[1.6rem]">
                <div className="w-[5.2rem] shrink-0 text-left font-semibold whitespace-nowrap">Rank</div>
                <div className="min-w-0 flex-[3] text-left font-semibold whitespace-nowrap">User</div>
                <div className="w-[14rem] shrink-0 text-left font-semibold whitespace-nowrap">Address</div>
                <div className="flex w-[7.2rem] shrink-0 items-center justify-end">
                  <span className="font-semibold whitespace-nowrap">Actions</span>
                </div>
              </div>

              <div className="flex flex-col space-y-[0.2rem]">
            {isLoading ? (
              Array.from({ length: 8 }).map((_, index) => (
                <div
                  key={`top30-skeleton-${index}`}
                  className="flex items-center gap-[1.2rem] rounded-[0.8rem] bg-[rgba(255,255,255,0.02)] px-[1.6rem] py-[1.2rem] lg:px-[1.2rem]"
                >
                  <Pulse className="h-[1.6rem] w-[3.2rem] shrink-0" />
                  <div className="flex min-w-0 flex-[3] items-center gap-[0.8rem]">
                    <Pulse className="h-[3.2rem] w-[3.2rem] shrink-0 rounded-full" />
                    <Pulse className="h-[2.8rem] w-[8rem] rounded-full" />
                    <Pulse className="h-[1.6rem] w-[10rem]" />
                  </div>
                  <Pulse className="h-[1.6rem] w-[12rem] shrink-0" />
                  <div className="flex w-[7.2rem] shrink-0 justify-end gap-[1rem]">
                    <Pulse className="h-[1.6rem] w-[1.6rem]" />
                    <Pulse className="h-[1.6rem] w-[1.6rem]" />
                  </div>
                </div>
              ))
            ) : uniqueTop30List.length > 0 ? (
              uniqueTop30List.map((address, index) => {
                const userId = getUserId(address);
                return (
                  <div
                    key={`${address}-${index}`}
                    className={`${
                      index < 10
                        ? 'bg-[rgba(175,127,255,0.10)]'
                        : index < 20
                        ? 'bg-[rgba(175,127,255,0.05)]'
                        : 'bg-[rgba(255,255,255,0.02)]'
                    } flex items-center gap-[1.2rem] py-[1.2rem] px-[1.6rem] text-[1.6rem] font-500 text-white leading-none lg:text-[1.4rem] lg:px-[1.2rem] rounded-[0.8rem]`}
                  >
                    <div className="w-[5.2rem] shrink-0 text-left">
                      <span className="text-white font-600">#{index + 1}</span>
                    </div>
                    <div className="flex min-w-0 flex-[3] items-center gap-[0.8rem] text-left">
                      <UserAvatar
                        src={profiles[address?.toLowerCase()]?.avatar}
                        nickname={profiles[address?.toLowerCase()]?.nickname}
                        className="h-[3.2rem] w-[3.2rem] shrink-0 text-[1.2rem] lg:h-[2.8rem] lg:w-[2.8rem]"
                      />
                      {userId ? <UserIdBlock id={userId} /> : null}
                      {profiles[address?.toLowerCase()]?.nickname ? (
                        <span className="min-w-0 truncate text-[1.4rem] font-500 leading-none text-white lg:text-[1.2rem]">
                          {profiles[address.toLowerCase()].nickname}
                        </span>
                      ) : null}
                    </div>
                    <div className="w-[14rem] shrink-0 text-left">
                      <span className="text-white font-400">{shortenAddress(address)}</span>
                    </div>
                    <div className="flex w-[7.2rem] shrink-0 items-center justify-end space-x-[1rem]">
                      <button
                        className="custom-transition hover:opacity-70"
                        onClick={() => copy(address)}
                        title="Copy address"
                      >
                        <img className="w-[1.6rem] h-[1.6rem] lg:w-[1.4rem] lg:h-[1.4rem]" src={whiteCopyIcon} alt="Copy" />
                      </button>
                      <a
                        href={`${explorerLinks.accountUrl}${address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="custom-transition hover:opacity-70"
                        title="View on explorer"
                      >
                        <img className="w-[1.6rem] h-[1.6rem] lg:w-[1.4rem] lg:h-[1.4rem]" src={linkIcon} alt="View" />
                      </a>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex items-center justify-center py-[4rem] text-white-500 text-[1.6rem] font-400">
                No data available
              </div>
            )}
              </div>
            </div>
          </div>
          {canScroll.right ? (
            <div className="pointer-events-none absolute inset-y-0 right-0 w-[5.6rem] bg-[linear-gradient(270deg,rgba(12,12,14,0.95)_0%,rgba(12,12,14,0)_100%)]" />
          ) : null}
          {canScroll.bottom ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[5.6rem] bg-[linear-gradient(0deg,rgba(12,12,14,0.95)_0%,rgba(12,12,14,0)_100%)]" />
          ) : null}
        </div>
      </div>
    </div>
  );
};

